import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { adminLogin } from "@/admin/api";
import { setAdminToken } from "@/admin/auth";

export default function AdminLogin() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");

    if (!email.trim() || !password.trim()) {
      setError(t("requiredField"));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await adminLogin({ email, password });
      setAdminToken(result.token, rememberMe);
      navigate("/admin");
    } catch {
      setError(t("adminInvalidCredentials"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F3EF] relative overflow-hidden flex items-center justify-center px-4">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(900px_circle_at_15%_20%,rgba(232,146,124,0.24),transparent_60%),radial-gradient(700px_circle_at_85%_10%,rgba(42,157,143,0.2),transparent_55%),radial-gradient(600px_circle_at_50%_90%,rgba(233,196,106,0.18),transparent_50%)]" />
      <div className="relative w-full max-w-2xl bg-white/75 backdrop-blur-xl rounded-3xl shadow-[0_30px_80px_-60px_rgba(15,23,42,0.6)] border border-white/60 p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-primary-400 text-white flex items-center justify-center">
            <i className="ri-shield-star-line text-xl" />
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold text-navy-400">{t("adminLoginTitle")}</h1>
          <p className="text-sm text-gray-500 leading-relaxed">{t("adminLoginSubtitle")}</p>
          <div className="bg-white/80 rounded-2xl border border-white/70 p-4 text-xs text-gray-500 space-y-2">
            <p>{t("adminSettingsHint")}</p>
            <p>{t("adminSmtpPresetHint")}</p>
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-navy-400 mb-4">{t("adminSignIn")}</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t("adminEmail")}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("adminEmailPlaceholder")}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/10 outline-none bg-white/70"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t("adminPassword")}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("adminPasswordPlaceholder")}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/10 outline-none bg-white/70"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            {t("adminRememberMe")}
          </label>
          {error && <p className="text-xs text-red-500">{error}</p>}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-4 py-3 rounded-xl bg-navy-400 hover:opacity-90 text-white text-sm font-semibold transition-colors shadow-[0_14px_30px_-20px_rgba(38,70,83,0.8)]"
          >
            {isSubmitting ? t("adminLoggingIn") : t("adminSignIn")}
          </button>
          </form>
        </div>
      </div>
    </div>
  );
}
